import React, { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Loader2 } from "lucide-react";

import OtpField from "../../../components/auth/OtpField";
import ResendTimer from "../../../components/auth/ResendTimer";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { API } from "../../../constant/api";
import useAuthAxios from "../../../utils/baseAxios";
import { errorMessage } from "../../../utils/errorMessage";

const RESEND_SECONDS = 59;

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Sent to request-otp — e.g. "change-password", "edit-profile". */
  purpose: string;
  title: string;
  description: string;
  confirmLabel?: string;
  /** Runs with the entered code. Throw / reject to keep the dialog open. */
  onConfirm: (otp: string) => Promise<unknown>;
};

/**
 * Both sensitive changes in settings — password and phone number — are gated
 * behind the same request-otp → submit-with-otp handshake, so the countdown,
 * resend and code entry live here once rather than in each caller.
 */
const OtpConfirmDialog = ({
  open,
  onOpenChange,
  purpose,
  title,
  description,
  confirmLabel = "Confirm",
  onConfirm,
}: Props) => {
  const axiosInstance = useAuthAxios();
  const [otp, setOtp] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(RESEND_SECONDS);
  // Guards against React 18 StrictMode double-invoking the open effect and
  // firing two OTP requests.
  const requestedFor = useRef<string | null>(null);

  const requestOtp = useMutation({
    mutationFn: async () => {
      const { data } = await axiosInstance.post(API.requestOTP, { purpose });
      return data;
    },
    onSuccess: () => setSecondsLeft(RESEND_SECONDS),
    onError: (e: any) =>
      toast.error(errorMessage(e?.response?.data?.message || e?.message)),
  });

  const confirm = useMutation({
    mutationFn: (code: string) => onConfirm(code),
    onSuccess: () => onOpenChange(false),
  });

  // Request a code as soon as the dialog opens, and reset when it closes.
  useEffect(() => {
    if (open) {
      if (requestedFor.current !== purpose) {
        requestedFor.current = purpose;
        requestOtp.mutate();
      }
    } else {
      requestedFor.current = null;
      setOtp("");
      setSecondsLeft(RESEND_SECONDS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, purpose]);

  useEffect(() => {
    if (!open || secondsLeft <= 0) return;
    const timer = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [open, secondsLeft]);

  const submit = (code: string) => {
    if (code.length < 6 || confirm.isPending) return;
    confirm.mutate(code);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="font-manrope mds:max-w-[420px]">
        <DialogHeader>
          <DialogTitle className="font-sora">{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <OtpField value={otp} onChange={setOtp} onComplete={submit} />

          <ResendTimer
            isRunning={secondsLeft > 0}
            label={`0:${String(secondsLeft).padStart(2, "0")}`}
            onResend={() => requestOtp.mutate()}
          />
        </div>

        <DialogFooter>
          <Button
            onClick={() => submit(otp)}
            disabled={otp.length < 6 || confirm.isPending}
            className="h-[52px] w-full rounded-xl text-[15px] font-semibold"
          >
            {confirm.isPending ? (
              <Loader2 className="size-5 animate-spin" />
            ) : (
              confirmLabel
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OtpConfirmDialog;
